/**
 * A/B Test Viewer Component
 * 
 * Displays side-by-side comparison of two ingestion prompt variations
 * Used for preference learning and prompt optimization
 */

import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { ScrollArea } from '@/components/ui/scroll-area';

interface ABTestOption {
  markdown?: string;
  frontmatter?: Record<string, any>;
  quality?: {
    score: number;
    breakdown: Record<string, number>;
    feedback: string[];
    suggestions: string[];
  };
  prompt: string;
  error?: string;
}

interface ABTestViewerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  optionA: ABTestOption | null;
  optionB: ABTestOption | null;
  isLoading?: boolean;
  onSelect: (choice: 'A' | 'B', reason?: string) => void;
}

export function ABTestViewer({
  open,
  onOpenChange,
  optionA,
  optionB,
  isLoading = false,
  onSelect
}: ABTestViewerProps) {
  const [selectedReason, setSelectedReason] = useState('');
  const [selectedChoice, setSelectedChoice] = useState<'A' | 'B' | null>(null);

  const handleSelect = (choice: 'A' | 'B') => {
    setSelectedChoice(choice);
    onSelect(choice, selectedReason || undefined);
  };

  const handleClose = () => {
    setSelectedChoice(null);
    setSelectedReason('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-[95vw] w-full h-[90vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="text-xl font-semibold">
            Compare Ingestion Options
          </DialogTitle>
          <DialogDescription>
            Review both options side-by-side. Click "Use This Version" to see a diff preview and apply changes to create or update the work spec.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Generating both options...</p>
            </div>
          </div>
        ) : optionA && optionB && optionA.markdown && optionB.markdown ? (
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Comparison Grid */}
            <div className="flex-1 grid grid-cols-2 gap-4 p-6 overflow-hidden">
              {/* Option A */}
              <div className="flex flex-col border rounded-lg overflow-hidden">
                <div className="px-4 py-3 border-b bg-muted/30 flex items-center justify-between">
                  <h3 className="font-semibold text-lg">Option A</h3>
                  {optionA.quality && (
                    <Badge variant={optionA.quality.score >= 90 ? 'default' : 'secondary'}>
                      Quality: {optionA.quality.score}%
                    </Badge>
                  )}
                </div>
                <ScrollArea className="flex-1 p-4">
                  <div className="prose prose-sm dark:prose-invert max-w-none">
                    <ReactMarkdown>{optionA.markdown}</ReactMarkdown>
                  </div>
                </ScrollArea>
                <div className="px-4 py-3 border-t bg-muted/30">
                  <Button
                    onClick={() => handleSelect('A')}
                    className="w-full"
                    variant={selectedChoice === 'A' ? 'default' : 'outline'}
                    disabled={selectedChoice !== null}
                    title="Select this option to see a diff preview and apply it to create or update the work spec"
                  >
                    {selectedChoice === 'A' ? (
                      <>
                        <CheckCircle2 className="mr-2 h-4 w-4" />
                        Selected
                      </>
                    ) : (
                      'Use This Version'
                    )}
                  </Button>
                </div>
              </div>

              {/* Option B */}
              <div className="flex flex-col border rounded-lg overflow-hidden">
                <div className="px-4 py-3 border-b bg-muted/30 flex items-center justify-between">
                  <h3 className="font-semibold text-lg">Option B</h3>
                  {optionB.quality && (
                    <Badge variant={optionB.quality.score >= 90 ? 'default' : 'secondary'}>
                      Quality: {optionB.quality.score}%
                    </Badge>
                  )}
                </div>
                <ScrollArea className="flex-1 p-4">
                  <div className="prose prose-sm dark:prose-invert max-w-none">
                    <ReactMarkdown>{optionB.markdown}</ReactMarkdown>
                  </div>
                </ScrollArea>
                <div className="px-4 py-3 border-t bg-muted/30">
                  <Button
                    onClick={() => handleSelect('B')}
                    className="w-full"
                    variant={selectedChoice === 'B' ? 'default' : 'outline'}
                    disabled={selectedChoice !== null}
                    title="Select this option to see a diff preview and apply it to create or update the work spec"
                  >
                    {selectedChoice === 'B' ? (
                      <>
                        <CheckCircle2 className="mr-2 h-4 w-4" />
                        Selected
                      </>
                    ) : (
                      'Use This Version'
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {/* Comparison Stats */}
            <div className="px-6 py-4 border-t bg-muted/30">
              <div className="grid grid-cols-2 gap-4 text-sm">
                {optionA.quality && (
                  <div>
                    <h4 className="font-medium mb-2">Option A Quality Breakdown</h4>
                    <div className="space-y-1">
                      {Object.entries(optionA.quality.breakdown).map(([key, value]) => (
                        <div key={key} className="flex justify-between">
                          <span className="text-muted-foreground capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}:</span>
                          <span className="font-medium">{Math.round(Number(value))}/10</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {optionB.quality && (
                  <div>
                    <h4 className="font-medium mb-2">Option B Quality Breakdown</h4>
                    <div className="space-y-1">
                      {Object.entries(optionB.quality.breakdown).map(([key, value]) => (
                        <div key={key} className="flex justify-between">
                          <span className="text-muted-foreground capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}:</span>
                          <span className="font-medium">{Math.round(Number(value))}/10</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Optional Reason Field */}
              {selectedChoice && (
                <div className="mt-4">
                  <label className="text-sm font-medium mb-2 block">
                    Why did you choose Option {selectedChoice}? (Optional)
                  </label>
                  <textarea
                    value={selectedReason}
                    onChange={(e) => setSelectedReason(e.target.value)}
                    placeholder="e.g., Better technical details, More concise, Better structure..."
                    className="w-full px-3 py-2 text-sm border rounded-md resize-none"
                    rows={2}
                  />
                </div>
              )}
            </div>
          </div>
        ) : optionA ? (
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Single Option View (when Option B is disabled/failed) */}
            <div className="flex-1 flex flex-col p-6 overflow-hidden">
              {/* Option A */}
              <div className="flex flex-col border rounded-lg overflow-hidden max-w-4xl mx-auto w-full">
                <div className="px-4 py-3 border-b bg-muted/30 flex items-center justify-between">
                  <h3 className="font-semibold text-lg">Generated Work Spec</h3>
                  {optionA.quality && (
                    <Badge variant={optionA.quality.score >= 90 ? 'default' : 'secondary'}>
                      Quality: {optionA.quality.score}%
                    </Badge>
                  )}
                </div>
                <ScrollArea className="flex-1 p-4">
                  <div className="prose prose-sm dark:prose-invert max-w-none">
                    <ReactMarkdown>{optionA.markdown}</ReactMarkdown>
                  </div>
                </ScrollArea>
                <div className="px-4 py-3 border-t bg-muted/30">
                  <Button
                    onClick={() => handleSelect('A')}
                    className="w-full"
                    variant={selectedChoice === 'A' ? 'default' : 'outline'}
                    disabled={selectedChoice !== null}
                  >
                    {selectedChoice === 'A' ? (
                      <>
                        <CheckCircle2 className="mr-2 h-4 w-4" />
                        Selected
                      </>
                    ) : (
                      'Use This Version'
                    )}
                  </Button>
                </div>
              </div>

              {/* Option B Error (if present) */}
              {optionB?.error && (
                <div className="mt-4 max-w-4xl mx-auto w-full">
                  <div className="px-4 py-3 border border-yellow-500/50 rounded-lg bg-yellow-500/10">
                    <p className="text-sm text-yellow-600 dark:text-yellow-400">
                      <strong>Option B unavailable:</strong> {optionB.error}
                    </p>
                  </div>
                </div>
              )}

              {/* Quality Stats */}
              {optionA.quality && (
                <div className="mt-4 max-w-4xl mx-auto w-full px-6 py-4 border-t bg-muted/30">
                  <h4 className="font-medium mb-2">Quality Breakdown</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {Object.entries(optionA.quality.breakdown).map(([key, value]) => (
                      <div key={key} className="flex justify-between">
                        <span className="text-muted-foreground capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}:</span>
                        <span className="font-medium">{Math.round(Number(value))}/10</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Optional Reason Field */}
              {selectedChoice && (
                <div className="mt-4 max-w-4xl mx-auto w-full px-6">
                  <label className="text-sm font-medium mb-2 block">
                    Any feedback? (Optional)
                  </label>
                  <textarea
                    value={selectedReason}
                    onChange={(e) => setSelectedReason(e.target.value)}
                    placeholder="e.g., Good technical details, Could be more concise..."
                    className="w-full px-3 py-2 text-sm border rounded-md resize-none"
                    rows={2}
                  />
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-muted-foreground">No options available</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

